import React from 'react';
import { useHistory } from 'react-router-dom';
import classnames from 'classnames';
import { useQuery } from '@apollo/react-hooks';
import { compact } from 'lodash';
import { appStage } from 'helpers';

import { ToggleSwitch, PlainButton, Modal } from 'components/shared';
import {
  SelectMenu,
  PopupButton,
  CreateMenu,
  UserProfile,
  DeveloperMenu,
  ImpersonateMenu,
} from '../';
import { trackingTheme } from 'services/analytics';

import { useTranslation } from 'hooks/useTranslation';
import { useAuthDispatch, useThemeDispatch, useThemeState } from 'context';
import { GetUserQuery } from 'services/apollo/queries';

import styles from './UserNav.module.scss';
import { ImpersonateModal } from 'screens';

interface SettingsAction {
  key: string;
  props?: any;
}

interface UserNavProps {
  type?: string;
  onlyDisplayLogout?: boolean;
}

export const UserNav: React.FC<UserNavProps> = ({ onlyDisplayLogout, type = 'Normal' }) => {
  const history = useHistory();
  const authDispatch = useAuthDispatch();
  const themeState = useThemeState();
  const themeDispatch = useThemeDispatch();
  const { t } = useTranslation();

  const [modalIsOpen, setIsOpen] = React.useState(false);

  const { data } = useQuery(GetUserQuery, {
    fetchPolicy: 'cache-and-network',
  });

  const isAllowedToImpersonate = data?.me?.roles?.canImpersonate;

  const getSettingsActions = (isAdmin: boolean) => {
    return compact([
      {
        key: 'manage_apps',
        value: 'Manage Apps',
        props: {
          to: `/manage-apps`,
        },
      },
      isAdmin && {
        key: 'settings',
        value: 'Settings',
        props: {
          to: `/settings`,
        },
      },
      {
        key: 'changeTheme',
        value: (
          <>
            {themeState.theme === 'light' ? 'Light Mode' : 'Dark Mode'}
            <ToggleSwitch
              type="round"
              isActive={themeState.theme === 'dark'}
              onClick={(e) => {
                handleThemeChange(e);
                trackingTheme(themeState.theme);
              }}
            />
          </>
        ),
        props: {
          div: 'true',
        },
      },
      {
        key: 'logout',
        value: 'Log Out',
      },
      appStage !== 'prod' && {
        key: 'dev_menu',
        value: <DeveloperMenu />,
        props: {
          to: `/developer`,
        },
      },
      isAllowedToImpersonate && {
        key: 'impersonate',
        value: <ImpersonateMenu />,
      },
    ]);
  };

  const handleMenuAction = (action: SettingsAction) => {
    switch (action.key) {
      case 'logout':
        authDispatch({ type: 'LOGOUT' });
        break;

      case 'manage_apps':
        history.push(`/manage-apps`);
        break;

      case 'dev_menu':
        history.push(action.props.to);
        break;

      case 'settings':
        break;

      case 'impersonate':
        setIsOpen(true);
        break;

      default:
        break;
    }
  };

  /**
   * Called when theme toggle is clicked
   * @param e
   */
  const handleThemeChange = (e: any) => {
    e.stopPropagation();
    e.preventDefault();
    themeDispatch({ type: 'TOGGLE_THEME' });
  };

  return (
    <div className={classnames(styles.container, styles[`type-${type}`])}>
      <UserProfile type={type} />

      <PopupButton
        id="sidebar"
        buttonActiveClass={styles.createButtonActive}
        buttonClassName={classnames('hasTooltip tooltipBelowCenter', styles.createButton)}
        buttonProps={{ type: 'Simple' }}
        className={classnames(styles.userNavButtonWrapper, styles.createButtonWrapper)}
        closeOnClick
        label={t('navigation-bar.create-btn.label')}
        popupClassName={styles.createPopup}
        tooltipText={t('navigation-bar.create-btn.tooltip')}
      >
        <div className={styles.createPopupWindow}>
          <CreateMenu title="Create New" />
        </div>
      </PopupButton>

      <PlainButton
        className={classnames('typeform-share button hasTooltip tooltipBelowRight', styles.helper)}
        href={`https://hnshah.typeform.com/to/D4Oktl?email=${data?.me.userProfile.email}`}
        data-tooltip="Click to get help"
        data-mode="popup"
        data-hide-headers="true"
        data-hide-footer="true"
        data-submit-close-delay="5"
        target="_blank"
      >
        ?
      </PlainButton>

      <SelectMenu
        buttonClassName="hasTooltip tooltipBelowRight"
        className={classnames(styles.userNavButtonWrapper, styles.settingsWrapper)}
        closeOnClick
        hideLabel
        horizontalPosition="Right"
        id="userMenu"
        menuClassName={styles.settingsMenu}
        onChange={handleMenuAction}
        options={getSettingsActions(data?.me.organization.isAdmin ? true : false)}
        popupClassName={styles.settingsPopup}
        tooltipText="Settings"
        onlyDisplayLogout={onlyDisplayLogout}
      />
      <Modal isOpen={modalIsOpen} onRequestClose={() => setIsOpen(false)}>
        <ImpersonateModal />
      </Modal>
    </div>
  );
};

export default UserNav;
